import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ClipboardList, Upload, CheckCircle2, ArrowLeft, ImagePlus,
  Compass, Eye, EyeOff, PenLine, Brain, Rocket, Send
} from 'lucide-react';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const CourseCreation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    courseCode: '',
    description: '',
    duration: '45',
    category: 'Capacity',
    banner: null,
    status: 'active',
    // ── SMAART Programme Classification ──
    programmeType: 'HumanIntelligence',
    stage: 'Stage1_Capacity',
    courseNumber: '',          // e.g. S01, PIQ01, AIQ03
    orderInStage: 1,           // position within stage
    unlockAfterCourseNumber: '', // for track unlock rules
  });

  const PROGRAMME_TYPES = [
    { value: 'HumanIntelligence', icon: Brain, label: 'Human Intelligence (Core)', desc: 'S01–S25 · 3 Stages' },
    { value: 'ReadinessTrack', icon: Rocket, label: 'Readiness Track Course', desc: 'PIQ, AIQ, SQ · Specialized' },
  ];

  const HUMAN_INTELLIGENCE_STAGES = [
    { value: 'Stage1_Capacity', label: 'Stage 1 — Capacity', courses: 10, range: 'S01–S10', category: 'Capacity' },
    { value: 'Stage2_Capability', label: 'Stage 2 — Capability', courses: 9, range: 'S11–S19', category: 'Capability' },
    { value: 'Stage3_Leadership', label: 'Stage 3 — Leadership', courses: 6, range: 'S20–S25', category: 'Leadership' },
  ];

  const READINESS_TRACK_STAGES = [
    { value: 'PIQ', label: 'Personal Intelligent Quotient (PIQ)', courses: 5, range: 'PIQ01–PIQ05', category: 'PIQ' },
    { value: 'AIQ', label: 'Artificial Intelligent Quotient (AIQ)', courses: 5, range: 'AIQ01–AIQ05', category: 'AIQ' },
    { value: 'SQ', label: 'Sustainability Quotient (SQ)', courses: 5, range: 'SQ01–SQ05', category: 'SQ' },
  ];

  const COURSE_CATALOG = {
    Stage1_Capacity: [
      { number: 'S01', title: 'Self-Awareness Foundations', desc: 'Understanding your strengths and areas for growth' },
      { number: 'S02', title: 'Emotional Intelligence Basics', desc: 'Managing emotions for better decision making' },
      { number: 'S03', title: 'Time Management Mastery', desc: 'Optimizing your productivity and focus' },
      { number: 'S04', title: 'Critical Thinking Fundamentals', desc: 'Analyzing information objectively' },
      { number: 'S05', title: 'Problem Solving Frameworks', desc: 'Structured approaches to complex challenges' },
      { number: 'S06', title: 'Learning How to Learn', desc: 'Meta-cognition and effective study techniques' },
      { number: 'S07', title: 'Stress Management', desc: 'Building resilience under pressure' },
      { number: 'S08', title: 'Goal Setting & Planning', desc: 'Creating actionable personal development plans' },
      { number: 'S09', title: 'Communication Basics', desc: 'Foundational verbal and written skills' },
      { number: 'S10', title: 'Digital Literacy', desc: 'Essential technology skills for the modern world' },
    ],
    Stage2_Capability: [
      { number: 'S11', title: 'Team Collaboration', desc: 'Working effectively in diverse teams' },
      { number: 'S12', title: 'Advanced Communication', desc: 'Persuasion, negotiation and influence' },
      { number: 'S13', title: 'Conflict Resolution', desc: 'Managing disagreements constructively' },
      { number: 'S14', title: 'Project Management Basics', desc: 'Planning and executing projects successfully' },
      { number: 'S15', title: 'AI Fundamentals', desc: 'Understanding artificial intelligence capabilities' },
      { number: 'S16', title: 'Data Literacy', desc: 'Interpreting and using data effectively' },
      { number: 'S17', title: 'Creative Thinking', desc: 'Generating innovative solutions' },
      { number: 'S18', title: 'Decision Making Under Uncertainty', desc: 'Making choices with incomplete information' },
      { number: 'S19', title: 'Networking & Relationship Building', desc: 'Creating professional connections' },
    ],
    Stage3_Leadership: [
      { number: 'S20', title: 'Leadership Fundamentals', desc: 'Core principles of effective leadership' },
      { number: 'S21', title: 'Strategic Thinking', desc: 'Long-term planning and vision' },
      { number: 'S22', title: 'Change Management', desc: 'Leading through organizational change' },
      { number: 'S23', title: 'Ethical Leadership', desc: 'Making principled decisions' },
      { number: 'S24', title: 'Mentoring & Coaching', desc: 'Developing others' },
      { number: 'S25', title: 'Executive Presence', desc: 'Projecting confidence and authority' },
    ],
    PIQ: [
      { number: 'PIQ01', title: 'Growth Mindset', desc: 'Embracing challenges and learning from failure' },
      { number: 'PIQ02', title: 'Confidence Building', desc: 'Developing self-assurance and presence' },
      { number: 'PIQ03', title: 'Motivation Mastery', desc: 'Understanding and driving personal motivation' },
      { number: 'PIQ04', title: 'Adaptability', desc: 'Thriving in changing environments' },
      { number: 'PIQ05', title: 'Personal Branding', desc: 'Defining and communicating your value' },
    ],
    AIQ: [
      { number: 'AIQ01', title: 'AI Tools & Applications', desc: 'Practical AI tools for everyday work' },
      { number: 'AIQ02', title: 'Prompt Engineering', desc: 'Communicating effectively with AI systems' },
      { number: 'AIQ03', title: 'AI Ethics & Safety', desc: 'Responsible AI usage guidelines' },
      { number: 'AIQ04', title: 'AI-Powered Creativity', desc: 'Using AI for creative problem solving' },
      { number: 'AIQ05', title: 'Future of Work with AI', desc: 'Preparing for AI-integrated workplaces' },
    ],
    SQ: [
      { number: 'SQ01', title: 'Sustainability Fundamentals', desc: 'Understanding environmental and social impact' },
      { number: 'SQ02', title: 'Ethical Decision Making', desc: 'Framework for principled choices' },
      { number: 'SQ03', title: 'Social Responsibility', desc: 'Individual and collective impact' },
      { number: 'SQ04', title: 'Sustainable Innovation', desc: 'Creating solutions that last' },
      { number: 'SQ05', title: 'Global Citizenship', desc: 'Thinking beyond borders and generations' },
    ]
  };

  const fetchCourse = useCallback(async () => {
    try {
      const { data } = await api.get(`/courses/${id}`);
      if (data.success) {
        const d = data.data;
        setFormData({
          title: d.title,
          courseCode: d.courseCode,
          description: d.description,
          duration: d.duration,
          category: d.category || 'Capacity',
          banner: d.banner,
          status: d.status,
          programmeType: d.programmeType || 'HumanIntelligence',
          stage: d.stage || 'Stage1_Capacity',
          courseNumber: d.courseNumber || '',
          orderInStage: d.orderInStage || 1,
          unlockAfterCourseNumber: d.unlockAfterCourseNumber || '',
        });
      }
    } catch (error) {
      toast.error('Failed to fetch course');
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchCourse();
    }
  }, [id, fetchCourse]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = async (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];

      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setUploading(true);
      const formDataFile = new FormData();
      formDataFile.append('image', file);

      try {
        const { data } = await api.post('/upload/image', formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (data.success) {
          setFormData({ ...formData, banner: data.data.url });
          toast.success('Banner uploaded successfully');
        }
      } catch (error) {
        toast.error('File upload failed');
      } finally {
        setUploading(false);
      }
    }
  };

  const validateForm = () => {
    if (!formData.title) {
      toast.error('Course title is required');
      return false;
    }
    if (!formData.description) {
      toast.error('Description is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        createdBy: user._id,
      };

      let response;
      if (id) {
        response = await api.put(`/courses/${id}`, payload);
        toast.success('Course updated successfully');
      } else {
        response = await api.post('/courses', payload);
        toast.success('Course created successfully');
      }

      if (response.data.success) {
        const courseId = response.data.data._id;
        navigate(`/courses/${courseId}/builder`);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Operation failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/courses');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#00152E] dark:to-[#002147] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4 bg-white dark:bg-[#002147]/40 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-[#1a3884]/40">
          <button
            onClick={handleCancel}
            className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-[#4f7dff] hover:bg-blue-50 dark:hover:bg-[#1a3884]/40 hover:border-[#1a3884] dark:hover:border-[#4f7dff] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
            title="Back to Courses"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {id ? 'Edit Course Settings' : 'Create New Course'}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Configure the core details and SMAART classification</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-xl bg-blue-50 dark:bg-[#1a3884]/25 border border-blue-100 dark:border-[#1a3884]/50">
            <span className={`w-2 h-2 rounded-full ${formData.status === 'active' ? 'bg-emerald-500' : formData.status === 'draft' ? 'bg-amber-500' : 'bg-rose-500'}`} />
            <span className="text-xs font-semibold text-[#1a3884] dark:text-blue-300 capitalize">{formData.status}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column: Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* SMAART Classification */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-[0_2px_10px_-3px_rgba(26,56,132,0.12)] dark:shadow-blue-900/20 border border-slate-100 dark:border-[#1a3884]/40 p-6 sm:p-8 transition-shadow hover:shadow-[0_4px_16px_-3px_rgba(26,56,132,0.16)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-blue-50 dark:bg-[#1a3884]/30 text-[#1a3884] dark:text-[#4f7dff] rounded-xl">
                  <Compass className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-white">SMAART Classification</h2>
              </div>

              <div className="space-y-6">
                {/* Programme Type */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Programme Track</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {PROGRAMME_TYPES.map(pt => {
                      const Icon = pt.icon;
                      const isSelected = formData.programmeType === pt.value;
                      return (
                        <button key={pt.value} type="button"
                          onClick={() => {
                            const isCore = pt.value === 'HumanIntelligence';
                            setFormData(prev => ({
                              ...prev,
                              programmeType: pt.value,
                              stage: isCore ? 'Stage1_Capacity' : 'PIQ',
                              category: isCore ? 'Capacity' : 'PIQ'
                            }));
                          }}
                          className={`flex items-start gap-3 text-left px-5 py-4 rounded-xl border-2 transition-all duration-200 ${
                            isSelected
                              ? 'border-[#1a3884] bg-[#1a3884]/5 dark:bg-[#1a3884]/20 shadow-sm scale-[1.01]'
                              : 'border-slate-100 dark:border-[#1a3884]/50 bg-slate-50 dark:bg-[#00152E] hover:border-[#4f7dff]/60 dark:hover:border-[#4f7dff] hover:bg-white dark:hover:bg-[#002147] hover:shadow-sm'
                          }`}
                        >
                          <div className={`p-2 rounded-lg shrink-0 ${isSelected ? 'bg-[#1a3884] text-white' : 'bg-white dark:bg-[#002147] text-[#1a3884] dark:text-[#4f7dff] border border-slate-200 dark:border-[#1a3884]/50'}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className={`font-bold text-sm ${isSelected ? 'text-[#1a3884] dark:text-[#8fb0ff]' : 'text-slate-700 dark:text-slate-300'}`}>{pt.label}</p>
                            <p className={`text-xs mt-1 ${isSelected ? 'text-[#1a3884]/70 dark:text-[#4f7dff]' : 'text-slate-500 dark:text-slate-400'}`}>{pt.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Stage Selection */}
                {formData.programmeType === 'HumanIntelligence' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Intelligence Stage</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {HUMAN_INTELLIGENCE_STAGES.filter(s => s.value !== 'Track').map(s => (
                        <button key={s.value} type="button"
                          onClick={() => setFormData(prev => ({ ...prev, stage: s.value, category: s.category || prev.category }))}
                          className={`relative text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                            formData.stage === s.value
                              ? 'border-[#1a3884] bg-[#1a3884]/5 dark:bg-[#1a3884]/20 shadow-sm scale-[1.01]'
                              : 'border-slate-100 dark:border-[#1a3884]/50 bg-slate-50 dark:bg-[#00152E] hover:border-[#4f7dff]/60 dark:hover:border-[#4f7dff] hover:bg-white dark:hover:bg-[#002147] hover:shadow-sm'
                          }`}
                        >
                          {formData.stage === s.value && (
                            <CheckCircle2 className="absolute top-2.5 right-2.5 w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                          )}
                          <p className={`font-bold text-sm pr-5 ${formData.stage === s.value ? 'text-[#1a3884] dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>{s.label}</p>
                          <p className={`text-xs mt-0.5 ${formData.stage === s.value ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-slate-500 dark:text-slate-400'}`}>{s.range}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {formData.programmeType === 'ReadinessTrack' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Intelligence Course</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {READINESS_TRACK_STAGES.map(s => (
                        <button key={s.value} type="button"
                          onClick={() => setFormData(prev => ({ ...prev, stage: s.value, category: s.category || prev.category }))}
                          className={`relative text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                            formData.stage === s.value
                              ? 'border-[#1a3884] bg-[#1a3884]/5 dark:bg-[#1a3884]/20 shadow-sm scale-[1.01]'
                              : 'border-slate-100 dark:border-[#1a3884]/50 bg-slate-50 dark:bg-[#00152E] hover:border-[#4f7dff]/60 dark:hover:border-[#4f7dff] hover:bg-white dark:hover:bg-[#002147] hover:shadow-sm'
                          }`}
                        >
                          {formData.stage === s.value && (
                            <CheckCircle2 className="absolute top-2.5 right-2.5 w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                          )}
                          <p className={`font-bold text-sm pr-5 ${formData.stage === s.value ? 'text-[#1a3884] dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>{s.label}</p>
                          <p className={`text-xs mt-0.5 ${formData.stage === s.value ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-slate-500 dark:text-slate-400'}`}>{s.range}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Course Identifiers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4 border-t border-slate-100 dark:border-[#1a3884]/30">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Course Number</label>
                    <input type="text"
                      value={formData.courseNumber}
                      onChange={e => setFormData(prev => ({ ...prev, courseNumber: e.target.value.toUpperCase() }))}
                      placeholder={formData.programmeType === 'HumanIntelligence' ? 'S01' : formData.programmeType === 'PIQ' ? 'PIQ01' : formData.programmeType === 'AIQ' ? 'AIQ01' : 'SQ01'}
                      className="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] font-mono font-bold uppercase transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Order Sequence</label>
                    <input type="number" min="1" max="25"
                      value={formData.orderInStage}
                      onChange={e => setFormData(prev => ({ ...prev, orderInStage: parseInt(e.target.value) || 1 }))}
                      className="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] font-mono transition-all"
                    />
                  </div>
                </div>

                {formData.programmeType === 'ReadinessTrack' && (
                  <div className="pt-2">
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Unlock Prerequisite</label>
                    <input type="text"
                      value={formData.unlockAfterCourseNumber}
                      onChange={e => setFormData(prev => ({ ...prev, unlockAfterCourseNumber: e.target.value.toUpperCase() }))}
                      placeholder={formData.programmeType === 'PIQ' ? 'S05' : formData.programmeType === 'AIQ' ? 'S15' : 'S21'}
                      className="w-full sm:w-1/2 px-4 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] font-mono font-bold uppercase transition-all"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">Requires this course to be completed first</p>
                  </div>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-[0_2px_10px_-3px_rgba(26,56,132,0.12)] dark:shadow-blue-900/20 border border-slate-100 dark:border-[#1a3884]/40 p-6 sm:p-8 transition-shadow hover:shadow-[0_4px_16px_-3px_rgba(26,56,132,0.16)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-blue-50 dark:bg-[#1a3884]/30 text-[#1a3884] dark:text-[#4f7dff] rounded-xl">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-white">Basic Information</h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Course Title *</label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={(e) => {
                      const selectedCourseTitle = e.target.value;
                      const availableCourses = COURSE_CATALOG[formData.stage] || [];
                      const courseInfo = availableCourses.find(c => c.title === selectedCourseTitle);
                      if (courseInfo) {
                        setFormData(prev => ({
                          ...prev,
                          title: courseInfo.title,
                          description: courseInfo.desc,
                          courseNumber: courseInfo.number
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          title: selectedCourseTitle,
                          description: '',
                          courseNumber: ''
                        }));
                      }
                    }}
                    required
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] transition-all"
                  >
                    <option value="">Select a course...</option>
                    {(COURSE_CATALOG[formData.stage] || []).map(course => (
                      <option key={course.number} value={course.title}>
                        {course.number} - {course.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Comprehensive description of the course objectives..."
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] transition-all resize-none"
                  />
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Settings & Actions */}
          <div className="space-y-6">

            {/* Status & Settings */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-[0_2px_10px_-3px_rgba(16,185,129,0.1)] dark:shadow-emerald-900/20 border border-slate-100 dark:border-[#1a3884]/40 p-6 transition-shadow hover:shadow-[0_4px_16px_-3px_rgba(16,185,129,0.14)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                  <Send className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-white">Publishing</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Visibility Status</label>
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={() => setFormData({ ...formData, status: 'active' })}
                      className={`flex items-center p-3 rounded-xl border-2 transition-all duration-200 ${formData.status === 'active'
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 shadow-sm'
                        : 'border-slate-100 dark:border-[#1a3884]/50 bg-white dark:bg-[#00152E] text-slate-600 dark:text-slate-300 hover:border-emerald-200 dark:hover:border-emerald-700 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
                        }`}
                    >
                      <Eye className={`w-5 h-5 mr-3 ${formData.status === 'active' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`} />
                      <div className="text-left">
                        <p className="text-sm font-bold">Active</p>
                        <p className={`text-xs ${formData.status === 'active' ? 'text-emerald-600/80 dark:text-emerald-400/80' : 'text-slate-400 dark:text-slate-500'}`}>Visible to students</p>
                      </div>
                    </button>

                    <button type="button" onClick={() => setFormData({ ...formData, status: 'draft' })}
                      className={`flex items-center p-3 rounded-xl border-2 transition-all duration-200 ${formData.status === 'draft'
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 shadow-sm'
                        : 'border-slate-100 dark:border-[#1a3884]/50 bg-white dark:bg-[#00152E] text-slate-600 dark:text-slate-300 hover:border-amber-200 dark:hover:border-amber-700 hover:bg-amber-50/50 dark:hover:bg-amber-900/10'
                        }`}
                    >
                      <PenLine className={`w-5 h-5 mr-3 ${formData.status === 'draft' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500'}`} />
                      <div className="text-left">
                        <p className="text-sm font-bold">Draft</p>
                        <p className={`text-xs ${formData.status === 'draft' ? 'text-amber-600/80 dark:text-amber-400/80' : 'text-slate-400 dark:text-slate-500'}`}>Not visible yet</p>
                      </div>
                    </button>

                    <button type="button" onClick={() => setFormData({ ...formData, status: 'inactive' })}
                      className={`flex items-center p-3 rounded-xl border-2 transition-all duration-200 ${formData.status === 'inactive'
                        ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-300 shadow-sm'
                        : 'border-slate-100 dark:border-[#1a3884]/50 bg-white dark:bg-[#00152E] text-slate-600 dark:text-slate-300 hover:border-rose-200 dark:hover:border-rose-700 hover:bg-rose-50/50 dark:hover:bg-rose-900/10'
                        }`}
                    >
                      <EyeOff className={`w-5 h-5 mr-3 ${formData.status === 'inactive' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400 dark:text-slate-500'}`} />
                      <div className="text-left">
                        <p className="text-sm font-bold">Inactive</p>
                        <p className={`text-xs ${formData.status === 'inactive' ? 'text-rose-600/80 dark:text-rose-400/80' : 'text-slate-400 dark:text-slate-500'}`}>Hidden from all</p>
                      </div>
                    </button>
                  </div>
                </div>

              </div>
            </div>

            {/* Banner Upload */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-[0_2px_10px_-3px_rgba(236,72,153,0.1)] dark:shadow-pink-900/20 border border-slate-100 dark:border-[#1a3884]/40 p-6 transition-shadow hover:shadow-[0_4px_16px_-3px_rgba(236,72,153,0.14)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-xl">
                  <ImagePlus className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-white">Course Banner</h2>
              </div>

              <div className="space-y-4">
                {formData.banner ? (
                  <div className="relative group rounded-xl overflow-hidden border border-slate-200 dark:border-[#1a3884]/50">
                    <img
                      src={formData.banner}
                      alt="Course Banner"
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <label className="cursor-pointer px-4 py-2 bg-white text-slate-900 rounded-lg text-sm font-bold shadow-lg hover:scale-105 transition-transform">
                        Change Image
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={uploading} />
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-slate-200 dark:border-[#1a3884]/50 border-dashed rounded-xl cursor-pointer bg-slate-50 dark:bg-[#00152E] hover:bg-slate-100 dark:hover:bg-[#1a3884]/20 hover:border-slate-300 dark:hover:border-[#1a3884] transition-all">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 text-slate-400 dark:text-slate-500 mb-2" />
                      <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">{uploading ? 'Uploading...' : 'Click to upload banner'}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">PNG, JPG up to 5MB</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-slate-200 dark:border-[#1a3884]/40 p-6">
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={uploading}
                className="w-full py-3.5 text-base font-bold bg-[#1a3884] hover:bg-[#142c68] shadow-md hover:shadow-lg hover:shadow-[#1a3884]/25 transition-all rounded-xl text-white"
              >
                {id ? 'Save Changes' : 'Create Course & Proceed'}
              </Button>
              <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-4">
                You will be redirected to the Course Builder to construct the 8-step flow next.
              </p>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseCreation;
